/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { useCallback } from 'react'
import { useNavigate } from 'react-router'
import { useModal } from '../../../../contexts/ModalContext'
import { getMyTasks, getTask } from '../../../../services/api/tasks'
import { TaskStatus } from '../../../../constants/task'
import { WorkflowStepType, WorkflowType } from '../../../../constants/workflow'
import NextTaskModal from './NextTaskModal'
import TaskCompleteModal from '../complete/TaskCompleteModal'
import { TASK_ACTIONS } from '../../../../components/doc-preview/TaskComplete'
import type { Task } from '../../../../services/api/types'
import { useServiceContext } from '../../../../root'

/**
 * Hook for handling next task navigation after completing a task
 * Migrated from: lego/page/tasks/next-task/useNextTask.jsx
 */
export function useNextTask() {
  const navigate = useNavigate()
  const { showModal } = useModal()
  const { service } = useServiceContext()
  const auth = service.auth
  const currentUser = auth.getUser()

  /**
   * Check if there's a next task available and show modal
   */
  const checkNextTask = useCallback(async () => {
    if (!currentUser) return

    try {
      // Legacy uses pageSize: 1 - only fetch 1 task to check if there's a next one
      const data = await getMyTasks({
        sortBy: 'startDate',
        sortDesc: true,
        taskStatus: TaskStatus.OPEN,
        userId: currentUser.id,
        pageSize: 1,
        pageStart: 0
      })

      // If exactly 1 task returned, show the next task modal
      if (data?.elements && data.elements.length === 1) {
        const task = data.elements[0]
        showModal(<NextTaskModal nextTask={task} />)
      }
    } catch (error) {
      console.error('Failed to check next task:', error)
    }
  }, [currentUser, showModal])

  /**
   * Navigate to next task - either show file or task complete modal
   */
  const goToNextTask = useCallback(
    async (nextTask: Task, secondRun = false) => {
      // Extract file from task
      const file =
        nextTask.files && nextTask.files[0]
          ? nextTask.files[0].draftFsi || nextTask.files[0].targetFsi || nextTask.files[0].sourceFsi
          : null

      if (file && (file as any).type !== 'PLACEHOLDER') {
        // Handle training workflow special case
        if (nextTask.workflowStepType === WorkflowStepType.TRAINING && !secondRun) {
          // Try to fetch file details to ensure it exists
          try {
            await getTask(String(file.id))
            goToShowFile(file, nextTask)
          } catch (err: any) {
            // If 404, reload task and retry once
            if (err?.status === 404 || err?.body?.code === 404) {
              try {
                const updatedTask = await getTask(String(nextTask.id))
                goToNextTask(updatedTask, true)
              } catch (e) {
                // If still fails, show complete modal
                showModal(<TaskCompleteModal task={nextTask} type={TASK_ACTIONS.taskComplete} />)
              }
            } else {
              // Other errors, show complete modal
              showModal(<TaskCompleteModal task={nextTask} type={TASK_ACTIONS.taskComplete} />)
            }
          }
        } else {
          goToShowFile(file, nextTask)
        }
      } else {
        // No file - show task complete modal
        showModal(<TaskCompleteModal task={nextTask} type={TASK_ACTIONS.taskComplete} />)
      }
    },
    [navigate, showModal]
  )

  /**
   * Navigate to file preview with task context
   */
  const goToShowFile = useCallback(
    (file: any, task: Task) => {
      // Preserve checkmate context
      const searchParams = new URLSearchParams(window.location.search)

      // Determine route for QC_REVIEW
      const isQcReview = task.workflowType === WorkflowType.QC_REVIEW
      const basePath = isQcReview ? '/show-file-qc' : '/show-file'

      // Build params
      const params = new URLSearchParams()
      params.set('taskId', String(task.id))
      params.set('action', 'taskComplete')
      if (file.versionId) params.set('version', String(file.versionId))
      if (file.minorVersionId) params.set('minorVersion', String(file.minorVersionId))

      // Append preserved search params (e.g., c_selections, tenantUuid)
      Array.from(searchParams.entries()).forEach(([key, value]) => {
        if (!params.has(key)) {
          params.append(key, value)
        }
      })

      const path = `${basePath}/${file.id}?${params.toString()}`
      navigate(path)
    },
    [navigate, service]
  )

  return {
    checkNextTask,
    goToNextTask
  }
}

export default useNextTask
